import { DateTime, DateTimeUnit } from 'luxon';
import { SetterOrUpdater } from 'recoil';
import axios from 'axios';

export const toLuxonDate = (date: string | Date | undefined): DateTime => {
  if (!date) return DateTime.invalid('received undefined');
  return typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
};

/**
 * The US zones the CMS displays times in. They are IANA names rather than fixed offsets because
 * the offset is -5 (EST) or -4 (EDT) depending on the date being resolved, and a schedule read in
 * January must not be rendered with the offset that happens to be in effect today.
 */
export const EASTERN_TIME_ZONE = 'America/New_York';
export const PACIFIC_TIME_ZONE = 'America/Los_Angeles';

export const getZoneOffsetHours = (zone: string, at: DateTime = DateTime.now()): number => at.setZone(zone).offset / 60;

export const toGmtLabel = (offsetHours: number): string => `GMT${offsetHours < 0 ? '' : '+'}${offsetHours}`;

export const setGlobalTimeDiff = async (setDiff: SetterOrUpdater<number>): Promise<void> => {
  try {
    const response = await axios.get('https://worldtimeapi.org/api/ip');
    const currentGlobalTime = DateTime.fromISO(response.data.utc_datetime).toMillis();
    setDiff(Date.now() - currentGlobalTime);
  } catch (err) {
    console.error(err);
  }
};

export const getSecondsFormatted = (diff: number): string => {
  let seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;
  const prefix0 = (num: number) => (num < 10 ? `0${num}` : num);
  return `${minutes}:${prefix0(seconds)}`;
};

// Use with useInterval, and no, "Infinity" doesn't work :)
export const largeNumber = 2147483647; // any arbitrary large number will do

export const getISODateStringToEndOfDay = (date: string | number | undefined): string | undefined => {
  if (!date) {
    return undefined;
  }
  return (
    DateTime.fromMillis(typeof date === 'number' ? date : new Date(date).getTime())
      .endOf('day')
      .toISO({ includeOffset: false }) || ''
  );
};

export const toStartOfDayISO = (date: string | undefined): string | undefined => {
  return date ? date.split('T')[0] + 'T00:00:00.000Z' : date;
};

export const toEndOfDayISO = (date: string | undefined): string | undefined => {
  return date ? date.split('T')[0] + 'T23:59:59.999Z' : date;
};

export const isSameDateTime = (date1?: string, date2?: string, unit: DateTimeUnit = 'minute'): boolean => {
  if (!date1 || !date2) {
    return false;
  }
  return DateTime.fromISO(date1).hasSame(DateTime.fromISO(date2), unit);
};

export const dayOfTheWeekLocalized = (dayNumber: number, locale: string): string => {
  // Pick a random Sunday and add the number of days to it
  return DateTime.fromISO('2022-09-04T12:00:00.000Z').setLocale(locale).plus({ days: dayNumber }).toFormat('cccc');
};

export const isValidDate = (value: unknown | undefined | null, textValue?: string): boolean => {
  if (!value && !textValue) return true;
  return value !== undefined && toLuxonDate(value as string | Date).isValid;
};

export const isBefore = (a: unknown | undefined | null, b: unknown | undefined): boolean => {
  if (!a || !b) return false;
  return toLuxonDate(a as string | Date) < toLuxonDate(b as string | Date);
};

export const isAfter = (a: unknown | undefined | null, b: unknown | undefined): boolean => {
  if (!a || !b) return false;
  return toLuxonDate(a as string | Date) > toLuxonDate(b as string | Date);
};

export const toMinutes = (seconds: number): number => {
  return seconds / 60;
};

export const convertDate = (stringifiedDate: string): string => {
  if (isNaN(Date.parse(stringifiedDate))) {
    const [h, m, s] = stringifiedDate.slice(0, -1).split(':');

    if (h && m) {
      // Seconds and milliseconds have to be set explicitly, otherwise they keep the current wall
      // clock and re-converting an already converted HH:mm:ssZ value drifts by up to 59 seconds.
      const date = new Date().setUTCHours(parseInt(h), parseInt(m), parseInt(s) || 0, 0);

      return convertDate(new Date(date).toISOString());
    }

    return 'Invalid Date: ' + stringifiedDate;
  }

  // `hour12: false` maps to the h24 cycle, which serializes the midnight hour as 24:xx:xx and gets
  // rejected by the backend's zoned time validator. h23 has to replace it: hour12 takes precedence.
  return `${new Date(stringifiedDate).toLocaleTimeString('en-US', { hourCycle: 'h23', timeZone: 'UTC' })}Z`;
};

const EASTERN_WALL_CLOCK = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

/**
 * The hour an editor picked, as `HH:mm:ss` in `America/New_York`, with no zone suffix.
 *
 * An unzoned value is already that wall clock and is returned unchanged. `DateTime.fromISO`
 * would otherwise read it in the editor's own zone and shift it. A legacy `HH:mm:ssZ` or a full
 * ISO instant is resolved in Eastern Time, so a 6:55 AM hero stays 6:55 AM across DST.
 * Channels keep `convertDate`: their country path still reads an unzoned time as UTC.
 */
export const toEasternWallClock = (value: string): string => {
  const trimmed = value.trim();
  if (EASTERN_WALL_CLOCK.test(trimmed)) {
    return trimmed;
  }

  const instant = DateTime.fromISO(trimmed);
  if (!instant.isValid) {
    return `Invalid Date: ${value}`;
  }

  return instant.setZone(EASTERN_TIME_ZONE).toFormat('HH:mm:ss');
};
